import { useCallback } from 'react'
import { useProfileStore } from '../stores/useProfileStore'
import { format } from 'date-fns'
import { CURRENCY_SYMBOLS } from '../constants/currencies'

// Format mappings from user preferences to date-fns format strings
const DATE_FORMAT_MAP: Record<string, string> = {
  'MM/DD/YYYY': 'MM/dd/yyyy',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'YYYY/MM/DD': 'yyyy/MM/dd',
  'DD.MM.YYYY': 'dd.MM.yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'MMM DD, YYYY': 'MMM dd, yyyy',
  'DD MMM YYYY': 'dd MMM yyyy',
  'MMMM DD, YYYY': 'MMMM dd, yyyy',
  'DD MMMM YYYY': 'dd MMMM yyyy'
}

// Create a reverse map for DB format to UI format
const DB_TO_UI_DATE_FORMAT: Record<string, string> = {};
for (const [uiFormat, dbFormat] of Object.entries(DATE_FORMAT_MAP)) {
  DB_TO_UI_DATE_FORMAT[dbFormat] = uiFormat;
}

// Helper function to safely get date object from various inputs
const getDateObject = (date: Date | string | number): Date => {
  if (date instanceof Date) {
    return date
  } else if (typeof date === 'string' || typeof date === 'number') {
    const parsedDate = new Date(date)
    return isNaN(parsedDate.getTime()) ? new Date() : parsedDate
  }
  return new Date()
}

export function useFormatters() {
  const { settings } = useProfileStore()

  const formatDate = useCallback((date: Date | string | number): string => {
    try {
      const dateObj = getDateObject(date)
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date value:', date)
        return 'Invalid Date'
      }
      
      // Get user preference directly from settings object
      const userDateFormat = settings?.preferences?.date_format
      
      // Simply use the date format directly since it's already in date-fns format
      if (userDateFormat) {
        return format(dateObj, userDateFormat)
      }
      
      // Fallback for missing preferences - use UI format mapping
      const uiFormat = 'MM/DD/YYYY'
      const formatPattern = DATE_FORMAT_MAP[uiFormat]
      
      return format(dateObj, formatPattern)
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }, [settings?.preferences?.date_format])

  const formatTime = useCallback((date: Date | string | number): string => {
    try {
      const dateObj = getDateObject(date)
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date value:', date)
        return 'Invalid Time'
      }
      
      // Get user preference or fallback to default
      const timeFormat = settings?.preferences?.time_format
      
      // Simply use the time format directly since it's already in date-fns format
      if (timeFormat) {
        return format(dateObj, timeFormat)
      }
      
      // Fallback to 24h format
      const fallbackFormat = 'HH:mm'
      
      return format(dateObj, fallbackFormat)
    } catch (error) {
      console.error('Error formatting time:', error)
      return 'Invalid Time'
    }
  }, [settings?.preferences?.time_format])

  const formatDateTime = useCallback((
    date: Date | string | number
  ): string => {
    try {
      const dateObj = getDateObject(date)
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date value:', date)
        return 'Invalid Date'
      }
      
      // Format both parts using the specialized functions
      const formattedDate = formatDate(dateObj)
      const formattedTime = formatTime(dateObj)
      
      return `${formattedDate} ${formattedTime}`
    } catch (error) {
      console.error('Error formatting datetime:', error)
      return 'Invalid Date'
    }
  }, [formatDate, formatTime])

  const formatNumber = useCallback((value: number, options?: Intl.NumberFormatOptions): string => {
    try {
      // Get the actual number format from preferences
      const numberFormat = settings?.preferences?.number_format
      
      // Set locale based on the number format
      let locale = 'en-US'
      let customFormat = false
      
      if (numberFormat === '#,##0.00') {
        locale = 'en-US' // Use US locale for thousand_dot
      } else if (numberFormat === '#.##0,00') {
        locale = 'de-DE' // Use German locale for thousand_comma
      } else if (numberFormat === '# ##0,00') {
        locale = 'fr-FR' // Use French locale for space_comma
      } else if (numberFormat === '0.00') {
        // No separator format - handled specially
        customFormat = true
      } else if (numberFormat) {
        // If we have another format specified, try to use it
        customFormat = true
      }
      
      const defaultOptions: Intl.NumberFormatOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options
      }

      // Special handling for 'no_separator' format or 0.00
      if (customFormat && (numberFormat === '0.00' || numberFormat === 'no_separator')) {
        return value.toFixed(defaultOptions.maximumFractionDigits || 2)
      }
      
      // For Indian numbering system (lakh, crore)
      if (numberFormat === 'indian' || numberFormat === '#,##,###.##') {
        locale = 'en-IN'
      }

      return new Intl.NumberFormat(locale, defaultOptions).format(value)
    } catch (error) {
      console.error('Error formatting number:', error)
      return value.toString()
    }
  }, [settings?.preferences?.number_format])

  const formatCurrency = useCallback((amount: number, currency: string = 'USD'): string => {
    return formatNumber(amount, {
      style: 'currency',
      currency
    })
  }, [formatNumber])

  const formatCurrencyPreferences = useCallback((amount: number, currency: string = 'USD'): string => {
    try {
      // Get user currency preferences 
      const userCurrency = currency || settings?.preferences?.currency || 'USD';
      
      // Get the currency symbol from our predefined symbols
      const symbol = CURRENCY_SYMBOLS[userCurrency] || userCurrency;
      
      // Format the number part
      const formattedNumber = formatNumber(Math.abs(amount));
      
      // Add the sign and symbol
      const sign = amount < 0 ? '-' : '';
      return `${sign}${symbol}${formattedNumber}`;
    } catch (error) {
      console.error('Error formatting currency with preferences:', error);
      // Fallback to basic formatting
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    }
  }, [formatNumber, settings?.preferences?.currency])

  return {
    formatDate,
    formatTime,
    formatDateTime,
    formatNumber,
    formatCurrency,
    formatCurrencyPreferences
  }
}